import { ReactNode } from 'react';
import { Field, FieldProps, Form, useFormikContext } from 'formik';
import { FormField, MoneyField, SelectField, assets } from '@round/whitelabel-ui';
import * as yup from 'yup';
import styles from './PostForm.module.css';
import { CurrencyField } from '@round/whitelabel-ui';
import { RadioOptionCard } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import useCreatorSelect, { CreatorSelectOption } from './useCreatorSelect';
import omit from 'lodash/omit';
import { Banner } from '@round/whitelabel-ui';
import { CurrencyOption, useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { useConditionalCurrencies } from 'contexts/OptionsContext/useConditionalCurrencies';
import useProjectDetails from '../../useProjectDetails';
import { Breakdown } from '@round/whitelabel-ui';
import { InfoTooltip } from '@round/whitelabel-ui';
import { usePreviewPostCost } from '@round/creatorbase-helpers/api-hooks';
import cn from 'classnames';

const { Warning: WarningIcon } = assets;

export type PostFormValues = {
    identifier: string;
    influencer_account_identifier: string;
    cost: number | null | undefined;
    currency: CurrencyOption | undefined | null;
    isLivePost: boolean | undefined;
    creator: CreatorSelectOption | null;
};

export const getValidationSchema = (isPostCostEditable: boolean) => {
    const schema = yup.object({
        isLivePost: yup.boolean().required('Please, enter either influencer or post url'),
        identifier: yup.string().when('isLivePost', {
            is: true,
            then: (schema) => schema.required('Post field is required'),
        }),
        influencer_account_identifier: yup
            .string()
            .test('influencerRequired', 'Influencer field is required', (value, context) => {
                return context.parent['isLivePost'] || !!context.parent['creator'] || !!value;
            }),
        creator: yup
            .object()
            .nullable()
            .test('influencerRequired', 'Influencer field is required', (value, context) => {
                return context.parent['isLivePost'] || !!context.parent['influencer_account_identifier'] || !!value;
            }),
    });

    if (isPostCostEditable) {
        return schema.shape({ cost: yup.string().required('Cost is required') });
    }

    return schema;
};

type Props = {
    isPostCostEditable?: boolean;
    campaignCurrency?: number;
    platform?: creatorbase.Platform;
    // Fee props
    areFeesEnabled?: boolean;
    campaignId: number | undefined;
    creatorCostMismatchBanner?: ReactNode;
};

const PostForm = ({
    isPostCostEditable,
    campaignCurrency,
    platform,
    areFeesEnabled,
    campaignId,
    creatorCostMismatchBanner: creatorCostMismatchBannerProp,
}: Props) => {
    const { team } = useProjectDetails();

    // only select from project's client creators
    const { getProps } = useCreatorSelect({
        params: { platforms: platform, client_id: team?.client_id },
        enabled: typeof team?.client_id === 'number',
    });

    const { values, isSubmitting } = useFormikContext<PostFormValues>();
    const selectedCreatorAccount = getPlatformAccount(values.creator, platform);
    const { currencies, getCurrencyById, getCurrencyOptionsById } = useCurrencies();
    const creatorbaseCurrencies = useConditionalCurrencies(true);

    const {
        data: costPreview,
        isLoading: isPreviewLoading,
        error: costPreviewError,
    } = usePreviewPostCost(
        {
            campaign_id: campaignId,
            base_cost: values.cost ?? undefined,
            currency_id: values.currency?.value,
        },
        { enabled: areFeesEnabled && !!values.cost && !!values.currency?.value && !!campaignId }
    );

    const creatorCostMismatchBanner = creatorCostMismatchBannerProp ? (
        <div className={styles.currencyMismatchBanner}>{creatorCostMismatchBannerProp}</div>
    ) : null;

    return (
        <Form className={styles.form}>
            <Field name="isLivePost">
                {({ field: { value: isLivePost }, form }: FieldProps<PostFormValues['isLivePost'], PostFormValues>) => (
                    <div className={styles.postType}>
                        <RadioOptionCard
                            name="isLivePost"
                            label="Enter Influencer"
                            isSelected={isLivePost === false}
                            onClick={() => form.setFieldValue('isLivePost', false)}
                        >
                            {isLivePost === false && (
                                <div className={styles.nestedInputContainer} onClick={(e) => e.stopPropagation()}>
                                    {platform && platform !== 'youtube' ? (
                                        <>
                                            <SelectField
                                                name="creator"
                                                label=""
                                                inputValue={form.values.influencer_account_identifier}
                                                {...omit(
                                                    getProps({
                                                        onChange: (value) => {
                                                            form.setFieldValue('influencer_account_identifier', '');
                                                            const platformAccount = getPlatformAccount(value, platform);

                                                            const shouldNotPrefillCost =
                                                                !isPostCostEditable ||
                                                                !platformAccount ||
                                                                (!areFeesEnabled &&
                                                                    values.currency?.value !==
                                                                        platformAccount.currency_id);

                                                            if (shouldNotPrefillCost) {
                                                                return;
                                                            }

                                                            form.setFieldValue('cost', platformAccount.cost);
                                                            form.setFieldValue(
                                                                'currency',
                                                                getCurrencyOptionsById(platformAccount.currency_id)
                                                            );
                                                        },
                                                        formatOptionLabel: (option, meta) => {
                                                            const platformUsername = getPlatformAccount(
                                                                option,
                                                                platform
                                                            )?.platform_username;

                                                            if (meta.context === 'value') {
                                                                return platformUsername || option.label;
                                                            }

                                                            return (
                                                                <div className={styles.creatorOption}>
                                                                    <p className={styles.label}>{option.label}</p>
                                                                    {platformUsername && (
                                                                        <p className={styles.subLabel}>
                                                                            @{platformUsername}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                        onInputChange: (value, meta) => {
                                                            if (meta.action === 'input-change') {
                                                                form.setFieldValue(
                                                                    'influencer_account_identifier',
                                                                    value
                                                                );
                                                                form.setFieldValue('creator', null);
                                                            }
                                                        },
                                                    }),
                                                    ['value', 'inputValue']
                                                )}
                                            />

                                            {selectedCreatorAccount?.contact_details && (
                                                <p className={styles.contactDetails}>
                                                    <span className={styles.contactDetailsLabel}>Contact details:</span>
                                                    <br />
                                                    {selectedCreatorAccount.contact_details}
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <FormField
                                            className={styles.nestedInput}
                                            name="influencer_account_identifier"
                                            placeholder="Enter influencer handle or url"
                                        />
                                    )}
                                </div>
                            )}
                        </RadioOptionCard>

                        <RadioOptionCard
                            name="isLivePost"
                            label="Enter Post URL"
                            isSelected={!!isLivePost}
                            onClick={() => form.setFieldValue('isLivePost', true)}
                        >
                            {!!isLivePost && (
                                <div className={styles.nestedInputContainer} onClick={(e) => e.stopPropagation()}>
                                    <FormField
                                        className={styles.nestedInput}
                                        name="identifier"
                                        placeholder="Enter post URL"
                                    />
                                </div>
                            )}
                        </RadioOptionCard>
                    </div>
                )}
            </Field>

            {isPostCostEditable && (
                <div className={styles.costSection}>
                    {areFeesEnabled ? (
                        <>
                            <Breakdown.Container className={styles.costContainer}>
                                <div>
                                    <MoneyField
                                        name="cost"
                                        currencyFieldName="currency"
                                        label="Cost"
                                        disabled={isSubmitting}
                                        isCurrencyDisabled={isSubmitting}
                                        currencies={creatorbaseCurrencies}
                                    />
                                </div>

                                <Breakdown.Separator className={styles.separator} />

                                <Breakdown.Item isTotal>
                                    <Breakdown.Label>
                                        <span className={styles.row}>
                                            <span>Total Cost</span> <InfoTooltip>Includes estimated fees</InfoTooltip>
                                        </span>
                                    </Breakdown.Label>
                                    <Breakdown.Amount
                                        amount={values.cost ? costPreview?.cost : undefined}
                                        currency={getCurrencyById(campaignCurrency)}
                                        className={cn({ [styles.errorAmount]: costPreviewError })}
                                        isLoading={isPreviewLoading}
                                    />
                                </Breakdown.Item>
                            </Breakdown.Container>

                            {creatorCostMismatchBanner}

                            {costPreviewError && (
                                <Banner appearance="destructive" className={styles.banner}>
                                    <WarningIcon className={styles.errorIcon} />
                                    <p>Something went wrong fetching estimated fees</p>
                                </Banner>
                            )}
                        </>
                    ) : (
                        <div>
                            <CurrencyField
                                name="cost"
                                label="Cost"
                                placeholder="Enter cost"
                                currencies={currencies}
                                currency={campaignCurrency}
                                groupSeparator=","
                                decimalSeparator="."
                                decimalsLimit={2}
                            />
                            {creatorCostMismatchBanner}
                        </div>
                    )}
                </div>
            )}
        </Form>
    );
};

export function getPlatformAccount(creator: CreatorSelectOption | null | undefined, platform?: creatorbase.Platform) {
    if (!platform || platform === 'youtube' || !creator) {
        return null;
    }

    return creator.platforms[platform][0] ?? null;
}

export default PostForm;
